import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { GlassCard } from '../../components/GlassCard';
import BrandLogo from '../../components/BrandLogo';
import { Printer, Download, Scale, Check, AlertTriangle } from 'lucide-react';
import { arrayToCsvBlob, downloadBlob } from '../../lib/csv';

export default function BalanceSheet() {
  const { token, tenant } = useAuth();
  const toast = useToast();
  const { formatMoney, taxLabel } = useTenantFormat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    api.erpBalanceSheet(token).then(setData).catch(e => { if (e.status !== 401) toast.show(e.message, 'error'); }).finally(() => setLoading(false));
  }, [token, toast]);

  const exportCsv = () => {
    if (!data) return;
    const a = data.assets || {}; const l = data.liabilities || {}; const e = data.equity || {};
    const rows = [
      { section: 'Assets', line: 'Cash / Bank', amount: a.cash },
      { section: 'Assets', line: 'Accounts Receivable', amount: a.accounts_receivable },
      { section: 'Assets', line: 'Inventory', amount: a.inventory },
      { section: 'Assets', line: 'Total Assets', amount: a.total },
      { section: 'Liabilities', line: 'Accounts Payable', amount: l.accounts_payable },
      { section: 'Liabilities', line: `${taxLabel} Payable`, amount: l.gst_payable },
      { section: 'Liabilities', line: 'Total Liabilities', amount: l.total },
      { section: 'Equity', line: 'Retained Earnings', amount: e.retained_earnings },
      { section: 'Equity', line: 'Total Equity', amount: e.total },
    ];
    downloadBlob(arrayToCsvBlob(rows, ['section', 'line', 'amount'], ['Section', 'Line Item', 'Amount']), `balance-sheet-${new Date().toISOString().slice(0,10)}.csv`);
    toast.show('CSV downloaded', 'success');
  };

  const a = data?.assets || {}; const l = data?.liabilities || {}; const e = data?.equity || {};

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="report-balance-sheet-page">
      <div className="hidden print:block mb-6">
        <div className="flex items-center gap-3 mb-2">
          <BrandLogo size={32} theme="light" />
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">{tenant?.name || '1000AD'}</h1>
            <p className="text-gray-500 text-xs">Balance Sheet as of {data?.as_of || '—'} · Generated {new Date().toLocaleDateString('en-IN')}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-5 print:hidden">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center">
            <Scale size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-gray-900 font-extrabold text-xl">Balance Sheet</h1>
            <p className="text-gray-500 text-xs">As of {data?.as_of || '—'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} disabled={!data} data-testid="bs-export-csv" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-40"><Download size={14} /> CSV</button>
          <button onClick={() => window.print()} data-testid="bs-print" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-purple text-white text-sm font-semibold hover:opacity-90"><Printer size={14} /> Print</button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : data ? (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <GlassCard className="p-5" data-testid="bs-assets-card">
              <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-4">Assets</h3>
              <div className="space-y-3">
                <BsRow label="Cash / Bank" value={a.cash} fmt={formatMoney} />
                <BsRow label="Accounts Receivable" value={a.accounts_receivable} fmt={formatMoney} />
                <BsRow label="Inventory" value={a.inventory} fmt={formatMoney} />
                <div className="border-t border-gray-200 pt-3"><BsRow label="Total Assets" value={a.total} fmt={formatMoney} bold /></div>
              </div>
            </GlassCard>
            <GlassCard className="p-5" data-testid="bs-liabilities-card">
              <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-4">Liabilities</h3>
              <div className="space-y-3">
                <BsRow label="Accounts Payable" value={l.accounts_payable} fmt={formatMoney} />
                <BsRow label={`${taxLabel} Payable`} value={l.gst_payable} fmt={formatMoney} />
                <div className="border-t border-gray-200 pt-3"><BsRow label="Total Liabilities" value={l.total} fmt={formatMoney} bold /></div>
              </div>
              <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mt-5 mb-4">Equity</h3>
              <div className="space-y-3">
                <BsRow label="Retained Earnings" value={e.retained_earnings} fmt={formatMoney} />
                <div className="border-t border-gray-200 pt-3"><BsRow label="Total Equity" value={e.total} fmt={formatMoney} bold /></div>
              </div>
            </GlassCard>
          </div>
          <div className="flex items-center gap-2">
            {data.balances_ok ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-xs font-bold border border-emerald-200"><Check size={14} /> Balanced</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 text-red-500 text-xs font-bold border border-red-200"><AlertTriangle size={14} /> Not balanced</span>
            )}
          </div>
        </div>
      ) : (
        <GlassCard className="p-12 text-center"><p className="text-gray-500">No balance sheet data.</p></GlassCard>
      )}
    </div>
  );
}

function BsRow({ label, value, fmt, bold }) {
  return (
    <div className="flex justify-between items-center">
      <span className={`text-sm ${bold ? 'text-gray-900 font-extrabold' : 'text-gray-600'}`}>{label}</span>
      <span className={`tabular-nums text-sm ${bold ? 'text-gray-900 font-extrabold' : 'text-gray-700'}`}>{fmt(value)}</span>
    </div>
  );
}
